// ResponseViewer — renders the result of a single Try-it call.
// States: busy (skeleton), error (red panel), success (status badge + Body/Headers tabs).

import { useMemo, useState } from 'react';
import { Copy, Check, RotateCcw } from 'lucide-react';
import CodeBlock from '@/components/code/CodeBlock';
import type { TryResponse } from '@/hooks/useTryRequest';

interface Props {
    response: TryResponse | null;
    busy: boolean;
    error: string | null;
    onReset: () => void;
}

type Tab = 'body' | 'headers';

export default function ResponseViewer({ response, busy, error, onReset }: Props) {
    const [tab, setTab] = useState<Tab>('body');
    const [pretty, setPretty] = useState(true);
    const [copied, setCopied] = useState(false);

    const bodyDisplay = useMemo(() => {
        if (!response) return '';
        if (!pretty) return response.bodyRaw;
        if (typeof response.body === 'string') return response.body;
        try { return JSON.stringify(response.body, null, 2); }
        catch { return response.bodyRaw; }
    }, [response, pretty]);

    const handleCopy = async () => {
        if (!response) return;
        try {
            await navigator.clipboard.writeText(bodyDisplay);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch { /* */ }
    };

    if (busy) {
        return (
            <div className="rounded-md p-4 animate-pulse" style={{ backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)' }}>
                <div className="h-3 w-24 mb-3 rounded" style={{ backgroundColor: 'var(--border)' }} />
                <div className="h-3 w-full mb-2 rounded" style={{ backgroundColor: 'var(--border)' }} />
                <div className="h-3 w-3/4 mb-2 rounded" style={{ backgroundColor: 'var(--border)' }} />
                <div className="h-3 w-1/2 rounded" style={{ backgroundColor: 'var(--border)' }} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-md p-3 flex items-start justify-between gap-3"
                style={{ backgroundColor: 'var(--err-bg)', border: '1px solid var(--err-border-strong)' }}>
                <div className="text-xs" style={{ color: 'var(--err-light)' }}>
                    <div className="font-semibold mb-0.5" style={{ color: 'var(--err)' }}>Request gagal</div>
                    <div>{error}</div>
                </div>
                <button
                    onClick={onReset}
                    className="inline-flex items-center gap-1 text-xxs px-2 py-1 rounded text-muted hover:text-ink transition-colors"
                    style={{ backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)' }}
                    aria-label="Reset response"
                >
                    <RotateCcw className="h-3 w-3" /> Reset
                </button>
            </div>
        );
    }

    if (!response) return null;

    const statusColor = statusColorFor(response.status);

    return (
        <div className="rounded-md" style={{ backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between gap-3 px-3 py-2" style={{ borderBottom: '1px solid var(--border)' }}>
                <div className="flex items-center gap-3">
                    <span
                        className="font-mono text-xxs font-bold px-2 py-0.5 rounded"
                        style={{ backgroundColor: `${statusColor}22`, color: statusColor }}
                        aria-label={`Status ${response.status}`}
                    >
                        {response.status} {response.statusText || statusLabel(response.status)}
                    </span>
                    <span className="text-xxs text-ghost font-mono">{response.elapsedMs}ms</span>
                </div>
                <button
                    onClick={onReset}
                    className="inline-flex items-center gap-1 text-xxs px-2 py-1 rounded text-muted hover:text-ink transition-colors"
                    aria-label="Reset response"
                >
                    <RotateCcw className="h-3 w-3" /> Reset
                </button>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 px-2 pt-2">
                <TabBtn active={tab === 'body'} onClick={() => setTab('body')}>Body</TabBtn>
                <TabBtn active={tab === 'headers'} onClick={() => setTab('headers')}>Headers</TabBtn>
                <div className="flex-1" />
                {tab === 'body' && (
                    <>
                        <button
                            onClick={() => setPretty((p) => !p)}
                            className="text-xxs text-muted hover:text-ink transition-colors px-2 py-1 rounded"
                            style={{ backgroundColor: 'var(--surface-hover)' }}
                            aria-label="Toggle pretty/raw"
                        >
                            {pretty ? 'Raw' : 'Pretty'}
                        </button>
                        <button
                            onClick={handleCopy}
                            className="inline-flex items-center gap-1 text-xxs text-muted hover:text-ink transition-colors px-2 py-1 rounded"
                            aria-label="Salin response body"
                        >
                            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                            {copied ? 'Tersalin' : 'Salin'}
                        </button>
                    </>
                )}
            </div>

            <div className="p-2">
                {tab === 'body' ? (
                    bodyDisplay
                        ? <CodeBlock code={bodyDisplay} lang="json" showCopy={false} />
                        : <div className="px-3 py-4 text-xs text-ghost">Tidak ada body.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xxs font-mono">
                            <tbody>
                                {Object.entries(response.headers).map(([k, v]) => (
                                    <tr key={k} className="align-top">
                                        <td className="text-muted py-1 pr-3 whitespace-nowrap">{k}</td>
                                        <td className="text-ink py-1 break-all">{v}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
    return (
        <button
            onClick={onClick}
            className={`text-xxs font-semibold px-2 py-1 rounded transition-colors ${active ? 'text-ink' : 'text-ghost hover:text-muted'}`}
            style={active ? { backgroundColor: 'var(--surface-hover)' } : undefined}
        >
            {children}
        </button>
    );
}

function statusColorFor(status: number): string {
    if (status >= 200 && status < 300) return 'var(--m-get)';
    if (status >= 400 && status < 500) return 'var(--warn)';
    if (status >= 500) return 'var(--err)';
    return 'var(--muted)';
}

function statusLabel(code: number): string {
    const m: Record<number, string> = {
        200: 'OK', 201: 'Created', 204: 'No Content',
        400: 'Bad Request', 401: 'Unauthorized', 403: 'Forbidden', 404: 'Not Found',
        409: 'Conflict', 422: 'Unprocessable Entity', 429: 'Too Many Requests',
        500: 'Internal Server Error', 502: 'Bad Gateway', 503: 'Service Unavailable',
    };
    return m[code] || '';
}
